import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import findIndex from 'lodash-es/findIndex';
import sortBy from 'lodash-es/sortBy';
import { Subscription } from 'rxjs';
import { CheckoutFormComponent } from '../form/checkout-form.component';
@Component({
    selector: 'checkout-navigation',
    templateUrl: './checkout-navigation.html',
    styleUrls: ['./checkout-navigation.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, TranslationModule, SizeClassDirective, RouterLink]
})
export class CheckoutNavigationComponent implements OnInit, OnDestroy {
    steps: Checkout.IStep[] = [];
    private subscriptions = new Subscription();
    private onStepsUpdate?: () => void;
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private app: App,
        private checkoutFormComponent: CheckoutFormComponent,
        private timeoutService: TimeoutService
    ) {}
    ngOnInit() {
        this.onStepsUpdate = this.timeoutService.debounce(() => this._onStepsUpdate(), 100);
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.StepsSetUpdate).subscribe(() => this.onStepsUpdate?.()));
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.StepChange).subscribe(() => this.changeDetectorRef.detectChanges()));
        this.onStepsUpdate();
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    get isSamples(): boolean {
        return this.checkoutFormComponent.isSamples;
    }
    goTo(step: Checkout.IStep): void {
        if (step.stepGroup) {
            const currentStep = this.checkoutFormComponent.currentStep;
            if (step.stepGroup === currentStep.stepGroup) {
                return;
            }
        }
        step.goTo();
    }
    ifDotActive(step: Checkout.IStep): boolean {
        const currenStep = this.checkoutFormComponent.currentStep;
        const currentStepIndex = findIndex(
            this.steps,
            s => s === currenStep || !!(currenStep.stepGroup && s.stepGroup === currenStep.stepGroup)
        );
        const stepIndex = findIndex(this.steps, s => s === step);
        return stepIndex <= currentStepIndex;
    }
    stepTrackFn(index: number, step: Checkout.IStep): string {
        return step.name;
    }
    isEnabled(step: Checkout.IStep): boolean {
        if (this.ifDotActive(step)) {
            return true;
        }
        const stepIndex = findIndex(this.checkoutFormComponent.visibleStepsSequence, s => s === step);
        if (stepIndex > 0) {
            const previousStep = this.checkoutFormComponent.visibleStepsSequence[stepIndex - 1];
            return !!previousStep?.valid;
        }
        return true;
    }
    private _onStepsUpdate(): void {
        const steps = this.checkoutFormComponent.visibleStepsSequence;
        if (steps?.length) {
            const visibleSteps: Checkout.IStep[] = [];
            steps.forEach(step => {
                if (step.order) {
                    if (step.stepGroup) {
                        // we want to display only last step from group
                        const addedIndex = findIndex(visibleSteps, visibleStep => visibleStep.stepGroup === step.stepGroup);
                        if (addedIndex > -1) {
                            visibleSteps.splice(addedIndex, 1);
                        }
                        visibleSteps.push(step);
                    } else {
                        visibleSteps.push(step);
                    }
                }
            });
            this.steps = sortBy(visibleSteps, step => step.order);
        } else {
            this.steps = [];
        }
        this.changeDetectorRef.detectChanges();
    }
}
